export const BULK_DEFAULTS = { maxFiles: 50, maxSizeMB: 5, concurrency: 3 };
export const ALLOWED_EXT = ["pdf", "doc", "docx"];

export const fileExt = (name) => (name || "").split(".").pop().toLowerCase();
export const fileKey = (file) => `${file.name}|${file.size}`;

export const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Splits picked files into accepted ones and rejected ones with a reason.
// `existing` is the list already queued (counts towards the limit and dedupe).
export const validateFiles = (picked, existing = [], options = {}) => {
  const { maxFiles, maxSizeMB } = { ...BULK_DEFAULTS, ...options };
  const seen = new Set(existing.map(fileKey));
  const accepted = [];
  const rejected = [];
  let count = existing.length;

  picked.forEach((file) => {
    let reason = "";
    if (!ALLOWED_EXT.includes(fileExt(file.name))) {
      reason = "Only PDF, DOC or DOCX files are allowed";
    } else if (file.size > maxSizeMB * 1024 * 1024) {
      reason = `File is larger than ${maxSizeMB} MB`;
    } else if (seen.has(fileKey(file))) {
      reason = "Duplicate file";
    } else if (count >= maxFiles) {
      reason = `Limit of ${maxFiles} files reached`;
    }
    if (reason) {
      rejected.push({ file, reason, duplicate: reason === "Duplicate file" });
    } else {
      accepted.push(file);
      seen.add(fileKey(file));
      count += 1;
    }
  });
  return { accepted, rejected };
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9]{7,15}$/;

// Returns a map of field -> message; empty means the row is valid
export const validateRow = (row) => {
  const errors = {};
  if (!`${row.firstName || ""}`.trim()) errors.firstName = "Name is required";
  if (!EMAIL_RE.test(`${row.email || ""}`.trim())) errors.email = "Invalid email";
  if (!PHONE_RE.test(`${row.phone || ""}`.replace(/\D/g, "")))
    errors.phone = "Invalid phone";
  const exp = row.experience;
  if (exp !== "" && exp != null && !(Number(exp) >= 0 && Number(exp) <= 60))
    errors.experience = "Invalid experience";
  return errors;
};

// ready | fix | exists | skipped. A repeated email inside the batch is "exists".
export const rowStatus = (row, rows) => {
  if (row.skipped) return "skipped";
  const email = `${row.email || ""}`.trim().toLowerCase();
  const firstIndex = rows.findIndex(
    (r) => `${r.email || ""}`.trim().toLowerCase() === email
  );
  if (email && (row.exists || firstIndex !== rows.findIndex((r) => r.id === row.id)))
    return "exists";
  return Object.keys(validateRow(row)).length ? "fix" : "ready";
};

// Runs worker over items with at most `limit` in flight; a rejection in one
// worker never stops the rest.
export const runPool = async (items, limit, worker) => {
  let next = 0;
  const lanes = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      try {
        await worker(item);
      } catch (e) {
        /* worker reports its own failure */
      }
    }
  });
  await Promise.all(lanes);
};

const stripExtension = (name) => `${name}`.trim().replace(/\.[a-z0-9]+$/i, "");
const sanitizeFileName = (name) =>
  `${name}`.trim().toLowerCase().replace(/["']/g, "").replace(/[\s_-]+/g, "");

// Longest substring overlap (>= 4 chars) in either direction
const findBestPartialMatch = (targetNoExt, files) => {
  let best = null;
  let bestScore = 0;
  files.forEach((file) => {
    const fileNoExt = sanitizeFileName(stripExtension(file.name));
    if (fileNoExt.length < 4 || targetNoExt.length < 4) return;
    if (fileNoExt.includes(targetNoExt) || targetNoExt.includes(fileNoExt)) {
      const score = Math.min(fileNoExt.length, targetNoExt.length);
      if (score > bestScore) {
        bestScore = score;
        best = file;
      }
    }
  });
  return best;
};

// Finds the resume file for a ResumeFileName cell: exact name, sanitized name,
// name without extension, then best partial overlap. Returns the File or null.
export const matchResumeFile = (resumeFileName, files) => {
  const target = `${resumeFileName ?? ""}`.trim();
  if (!target || !files?.length) return null;
  const lower = target.toLowerCase().replace(/["']/g, "");
  const sanitized = sanitizeFileName(target);
  const noExt = sanitizeFileName(stripExtension(target));
  return (
    files.find((f) => f.name.toLowerCase() === lower) ||
    files.find((f) => sanitizeFileName(f.name) === sanitized) ||
    files.find((f) => sanitizeFileName(stripExtension(f.name)) === noExt) ||
    findBestPartialMatch(noExt, files) ||
    null
  );
};

const sanitizeText = (text) => `${text}`.trim().toLowerCase().replace(/[\s_-]+/g, " ");

// Matches a sheet job title against open jobs by title (case/separator-insensitive)
export const matchJobTitle = (jobTitle, jobs) => {
  const target = `${jobTitle ?? ""}`.trim();
  if (!target || !jobs?.length) return null;
  const wanted = sanitizeText(target);
  const job = jobs.find((j) => sanitizeText(j.title || "") === wanted);
  return job
    ? { matched: true, job, reason: "" }
    : { matched: false, job: null, reason: "No matching job opening" };
};
